import { useState, useEffect } from 'react';
import { ArrowLeft, Activity, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const ComplianceStatus = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState<any[]>([]);
  const [recentAlerts, setRecentAlerts] = useState<any[]>([]);
  const [complianceMetrics, setComplianceMetrics] = useState<any[]>([]);

  useEffect(() => {
    if (user) {
      fetchComplianceData();
    }
  }, [user]);

  const fetchComplianceData = async () => {
    try {
      // Fetch units with recent checklist data
      const { data: unitsData, error: unitsError } = await supabase
        .from('units')
        .select(`
          *,
          checklists(
            id,
            status,
            completed_at,
            updated_at
          )
        `);

      if (unitsError) throw unitsError;
      setUnits(unitsData || []);

      // Fetch recent audit events (alerts)
      const { data: alertsData, error: alertsError } = await supabase
        .from('audit_events')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(10);

      if (alertsError) throw alertsError;
      setRecentAlerts(alertsData || []);

      // Calculate compliance metrics
      calculateComplianceMetrics(unitsData || []);

    } catch (error: any) {
      toast({
        title: "Error loading compliance data",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateComplianceMetrics = (unitsData: any[]) => {
    // Calculate basic metrics from available data
    const totalChecklists = unitsData.reduce((acc, unit) => acc + (unit.checklists?.length || 0), 0);
    const completedChecklists = unitsData.reduce((acc, unit) => 
      acc + (unit.checklists?.filter((c: any) => c.status === 'completed').length || 0), 0);
    
    const complianceRate = totalChecklists > 0 ? Math.round((completedChecklists / totalChecklists) * 100) : 100;

    setComplianceMetrics([
      {
        category: 'Overall Compliance',
        score: complianceRate,
        status: complianceRate >= 95 ? 'Excellent' : complianceRate >= 85 ? 'Good' : 'Fair',
        trend: 'stable',
        issues: Math.max(0, unitsData.length - Math.floor(unitsData.length * 0.9)),
        lastCheck: 'Just now'
      },
      {
        category: 'Active Units',
        score: unitsData.length > 0 ? 100 : 0,
        status: unitsData.length > 0 ? 'Good' : 'Needs Setup',
        trend: 'stable',
        issues: 0,
        lastCheck: 'Just now'
      }
    ]);
  };

  const getScoreColor = (score: number) => {
    if (score >= 95) return 'text-green-400';
    if (score >= 85) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Compliant':
      case 'Excellent': return 'bg-green-500/20 text-green-300';
      case 'Good': return 'bg-blue-500/20 text-blue-300';
      case 'Fair':
      case 'Attention Needed': return 'bg-yellow-500/20 text-yellow-300';
      default: return 'bg-red-500/20 text-red-300';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'High': return 'bg-red-500/20 text-red-300';
      case 'Medium': return 'bg-yellow-500/20 text-yellow-300';
      case 'Low': return 'bg-blue-500/20 text-blue-300';
      default: return 'bg-gray-500/20 text-gray-300';
    }
  };

  const overallScore = complianceMetrics.length > 0 ? complianceMetrics[0].score : 0;

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
          <h1 className="heading-section mb-2">Real-Time Compliance Status</h1>
          <p className="text-muted-foreground">Monitor live compliance metrics and system-wide food safety status</p>
        </div>

        {/* Overall Compliance Score */}
        <Card className="glass-panel border-0 mb-8">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className={`text-6xl font-bold ${getScoreColor(overallScore)} mb-2`}>
                {overallScore}%
              </div>
              <div className="text-lg font-medium mb-2">Overall Compliance Score</div>
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Activity className="h-4 w-4" />
                <span>Last updated: 5 minutes ago</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Compliance Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {loading ? (
            Array.from({ length: 2 }).map((_, i) => (
              <Card key={i} className="glass-panel border-0">
                <CardHeader>
                  <Skeleton className="h-6 w-48" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-2 w-full mb-2" />
                  <Skeleton className="h-3 w-32" />
                </CardContent>
              </Card>
            ))
          ) : complianceMetrics.map((metric) => (
            <Card key={metric.category} className="glass-panel border-0">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{metric.category}</CardTitle>
                  <Badge className={getStatusColor(metric.status)}>
                    {metric.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className={`text-2xl font-bold ${getScoreColor(metric.score)}`}>
                      {metric.score}%
                    </span>
                    <div className="flex items-center gap-1">
                      <TrendingUp className={`h-4 w-4 ${
                        metric.trend === 'up' ? 'text-green-400' : 
                        metric.trend === 'down' ? 'text-red-400' : 'text-gray-400'
                      }`} />
                      <span className="text-sm text-muted-foreground capitalize">
                        {metric.trend}
                      </span>
                    </div>
                  </div>
                  
                  <Progress value={metric.score} className="h-2" />
                  
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{metric.issues} active issues</span>
                    <span>Updated {metric.lastCheck}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Units Overview */}
        <Card className="glass-panel border-0 mb-8">
          <CardHeader>
            <CardTitle>Units Overview</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between p-4 glass-panel border border-white/10 rounded-lg">
                    <Skeleton className="h-16 w-full" />
                  </div>
                ))}
              </div>
            ) : units.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">No units configured yet</p>
                <Link to="/units">
                  <Button className="cta-primary">Add Your First Unit</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {units.map((unit) => {
                  const completedCount = unit.checklists?.filter((c: any) => c.status === 'completed').length || 0;
                  const totalCount = unit.checklists?.length || 0;
                  const unitScore = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 100;
                  const status = unitScore >= 95 ? 'Compliant' : unitScore >= 85 ? 'Good' : 'Attention Needed';
                  
                  return (
                    <div key={unit.id} className="flex items-center justify-between p-4 glass-panel border border-white/10 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <div className={`text-xl font-bold ${getScoreColor(unitScore)}`}>
                            {unitScore}%
                          </div>
                          <div className="text-xs text-muted-foreground">Score</div>
                        </div>
                        <div>
                          <h4 className="font-medium mb-1">{unit.name}</h4>
                          <div className="flex items-center gap-2">
                            <Badge className={getStatusColor(status)}>
                              {status}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {unit.address || 'No address set'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="text-center">
                          <div className="text-blue-400 font-medium">{completedCount}</div>
                          <div className="text-xs text-muted-foreground">Completed</div>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-400 font-medium">{totalCount - completedCount}</div>
                          <div className="text-xs text-muted-foreground">Pending</div>
                        </div>
                        <Link to="/units">
                          <Button size="sm" variant="outline" className="glass-button">
                            View Details
                          </Button>
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="glass-panel border-0">
          <CardHeader>
            <CardTitle>Recent System Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-3 p-3">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <div className="flex-1">
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                  </div>
                ))}
              </div>
            ) : recentAlerts.length === 0 ? (
              <div className="text-center py-8">
                <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No recent activity to display</p>
                <p className="text-sm text-muted-foreground mt-2">System activity will appear here as you use the platform</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentAlerts.map((alert) => (
                  <div key={alert.id} className="flex items-start justify-between p-3 glass-panel border border-white/10 rounded-lg">
                    <div className="flex items-start gap-3">
                      <div className="p-1 rounded-full">
                        <Activity className="h-4 w-4 text-blue-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{alert.event_type}</span>
                          <Badge className="bg-blue-500/20 text-blue-300">
                            {alert.action}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-1">
                          {alert.entity_type} activity recorded
                        </p>
                        <span className="text-xs text-muted-foreground">
                          {new Date(alert.timestamp).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ComplianceStatus;